/**
 * Campaign Wizard — multi-step draft builder (OPUS-D05).
 *
 * Three-step flow:
 *   1. Basics      — name, template pick (offer_id resolves from template)
 *   2. Targeting   — countries overview
 *   3. Review      — confirm + create
 *
 * Form layer is a lightweight, controlled `useState` reducer + Zod-style
 * per-step validators from `./api`. No external form library is pulled in —
 * that keeps the D05 surface inside the three owned files and avoids
 * touching `package.json`, which is a FORBIDDEN file.
 *
 * Theme: light, emerald → sky gradient on the step header, Inter, 6–8px
 * rounded corners, zinc borders. No emojis, no competitor names.
 *
 * Owned file (D05): `apps/dash/src/features/campaign-wizard/Wizard.tsx`
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
	type BasicsPayload,
	type CampaignDraftResponse,
	type PayoutModel,
	type PayoutPayload,
	type CapsPayload,
	type TargetingPayload,
	type TemplateListItem,
	type WizardStep,
	finalizeDraft,
	listTemplates,
	parseCountryList,
	saveDraftStep,
	startDraft,
	validateBasics,
	validateReview,
	validateTargeting,
} from "./api";

// ---------------------------------------------------------------------------
// Step definitions (UI-level — 3 grouped steps over the API's 5 raw steps)
// ---------------------------------------------------------------------------

type UiStep = "basics" | "targeting" | "review";

const UI_STEPS: Array<{ key: UiStep; title: string; subtitle: string }> = [
	{
		key: "basics",
		title: "Basics",
		subtitle: "Name your campaign and pick a template",
	},
	{
		key: "targeting",
		title: "Targeting",
		subtitle: "Where should this campaign run",
	},
	{
		key: "review",
		title: "Review & create",
		subtitle: "Confirm the details before committing",
	},
];

// ---------------------------------------------------------------------------
// Form model
// ---------------------------------------------------------------------------

interface FormState {
	basics: BasicsPayload;
	targetingRaw: string;
	targeting: TargetingPayload;
	payout: PayoutPayload;
	caps: CapsPayload;
	confirmed: boolean;
}

const EMPTY_FORM: FormState = {
	basics: { name: "", offer_id: "" },
	targetingRaw: "",
	targeting: { countries: [] },
	payout: { model: "cpa", amount: 0, currency: "USD" },
	caps: {},
	confirmed: false,
};

type FieldErrors = Record<string, string>;

// ---------------------------------------------------------------------------
// Public props — the route file passes a redirect callback so this component
// stays framework-neutral and trivially testable.
// ---------------------------------------------------------------------------

export interface CampaignWizardProps {
	onCreated?: (campaignId: string) => void;
}

export function CampaignWizard({ onCreated }: CampaignWizardProps) {
	const queryClient = useQueryClient();
	const [step, setStep] = useState<UiStep>("basics");
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [errors, setErrors] = useState<FieldErrors>({});
	const [draft, setDraft] = useState<CampaignDraftResponse | null>(null);
	const [startError, setStartError] = useState<string | null>(null);

	// ---- Bootstrap a draft once when the wizard mounts ---------------------
	useEffect(() => {
		let alive = true;
		startDraft()
			.then((d) => {
				if (!alive) return;
				setDraft(d);
			})
			.catch((err: unknown) => {
				if (!alive) return;
				const message =
					err instanceof Error ? err.message : "Failed to start draft";
				setStartError(message);
			});
		return () => {
			alive = false;
		};
	}, []);

	// ---- Templates for step 1 ---------------------------------------------
	const templatesQuery = useQuery<TemplateListItem[], Error>({
		queryKey: ["campaign-templates", "list"],
		queryFn: () => listTemplates(),
		staleTime: 60_000,
	});

	// ---- Mutations ---------------------------------------------------------
	const saveStep = useMutation({
		mutationFn: async (args: { step: WizardStep; payload: unknown }) => {
			if (!draft) throw new Error("Draft not ready");
			return saveDraftStep(draft.id, args.step, args.payload);
		},
		onSuccess: (next) => {
			// StepSaveResponse is a subset of CampaignDraftResponse; merge over the
			// existing draft so tenant_id / created_at remain populated.
			setDraft((prev) => (prev ? { ...prev, ...next } : prev));
		},
	});

	const finalize = useMutation({
		mutationFn: async () => {
			if (!draft) throw new Error("Draft not ready");
			// The API also wants explicit payout + caps + review before finalize
			// can succeed. Push them with minimum-viable values so that finalize
			// only reports real validation errors to the operator.
			await saveDraftStep(draft.id, "payout", form.payout);
			await saveDraftStep(draft.id, "caps", form.caps);
			await saveDraftStep(draft.id, "review", { confirmed: true });
			return finalizeDraft(draft.id);
		},
		onSuccess: (result) => {
			toast.success("Campaign created", {
				description: result.campaign.name,
			});
			queryClient.invalidateQueries({ queryKey: ["campaigns"] }).catch(() => {});
			onCreated?.(result.campaign.id);
		},
		onError: (err: unknown) => {
			const message =
				err instanceof Error ? err.message : "Failed to create campaign";
			toast.error("Could not create campaign", { description: message });
		},
	});

	// ---- Handlers ----------------------------------------------------------
	function updateBasics(patch: Partial<BasicsPayload>) {
		setForm((s) => ({ ...s, basics: { ...s.basics, ...patch } }));
	}

	function pickTemplate(t: TemplateListItem) {
		updateBasics({ offer_id: t.template_code });
		if (!form.basics.name.trim()) {
			updateBasics({ name: t.name });
		}
	}

	async function handleNext() {
		setErrors({});
		if (step === "basics") {
			const res = validateBasics(form.basics);
			if (!res.ok) {
				setErrors(res.fields);
				return;
			}
			try {
				await saveStep.mutateAsync({ step: "basics", payload: form.basics });
				setStep("targeting");
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Failed to save step";
				toast.error("Could not save basics", { description: message });
			}
			return;
		}
		if (step === "targeting") {
			const countries = parseCountryList(form.targetingRaw);
			const targeting: TargetingPayload = { ...form.targeting, countries };
			const res = validateTargeting(targeting);
			if (!res.ok) {
				setErrors(res.fields);
				return;
			}
			setForm((s) => ({ ...s, targeting }));
			try {
				await saveStep.mutateAsync({ step: "targeting", payload: targeting });
				setStep("review");
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Failed to save step";
				toast.error("Could not save targeting", { description: message });
			}
			return;
		}
	}

	function handleBack() {
		setErrors({});
		if (step === "review") setStep("targeting");
		else if (step === "targeting") setStep("basics");
	}

	async function handleSubmit() {
		setErrors({});
		const res = validateReview({ confirmed: form.confirmed });
		if (!res.ok) {
			setErrors(res.fields);
			return;
		}
		await finalize.mutateAsync().catch(() => {
			/* error already toasted */
		});
	}

	// ---- Derived UI --------------------------------------------------------
	const activeStepIndex = UI_STEPS.findIndex((s) => s.key === step);
	const activeStep = UI_STEPS[activeStepIndex] ?? UI_STEPS[0]!;

	const selectedTemplate = useMemo<TemplateListItem | null>(() => {
		if (!form.basics.offer_id) return null;
		return (
			templatesQuery.data?.find(
				(t) => t.template_code === form.basics.offer_id,
			) ?? null
		);
	}, [form.basics.offer_id, templatesQuery.data]);

	const isSubmitting = saveStep.isPending || finalize.isPending;

	return (
		<div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-12">
			<WizardHeader
				step={activeStep}
				stepIndex={activeStepIndex}
				total={UI_STEPS.length}
			/>

			{startError ? (
				<ErrorBanner title="Could not start draft" message={startError} />
			) : null}

			<form
				className="flex flex-col gap-8"
				onSubmit={(e) => {
					e.preventDefault();
					if (step === "review") void handleSubmit();
					else void handleNext();
				}}
			>
				{step === "basics" ? (
					<StepBasics
						form={form}
						errors={errors}
						templates={templatesQuery.data ?? []}
						templatesLoading={templatesQuery.isLoading}
						templatesError={
							templatesQuery.isError
								? (templatesQuery.error?.message ?? "Failed to load templates")
								: null
						}
						onChangeBasics={updateBasics}
						onPickTemplate={pickTemplate}
					/>
				) : null}

				{step === "targeting" ? (
					<StepTargeting
						form={form}
						errors={errors}
						onChangeRaw={(raw) =>
							setForm((s) => ({ ...s, targetingRaw: raw }))
						}
						onChangePayout={(patch) =>
							setForm((s) => ({ ...s, payout: { ...s.payout, ...patch } }))
						}
					/>
				) : null}

				{step === "review" ? (
					<StepReview
						form={form}
						template={selectedTemplate}
						confirmed={form.confirmed}
						error={errors.confirmed ?? null}
						onToggleConfirm={(v) =>
							setForm((s) => ({ ...s, confirmed: v }))
						}
					/>
				) : null}

				<WizardFooter
					step={step}
					canGoBack={step !== "basics"}
					isSubmitting={isSubmitting}
					onBack={handleBack}
				/>
			</form>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Header — gradient bar, step indicator
// ---------------------------------------------------------------------------

function WizardHeader({
	step,
	stepIndex,
	total,
}: {
	step: { title: string; subtitle: string };
	stepIndex: number;
	total: number;
}) {
	const progressPct = ((stepIndex + 1) / total) * 100;
	return (
		<header className="flex flex-col gap-3">
			<div
				className="rounded-[8px] p-[1px]"
				style={{
					backgroundImage:
						"linear-gradient(90deg, var(--color-brand-emerald) 0%, var(--color-brand-sky) 100%)",
				}}
			>
				<div className="flex flex-col gap-2 rounded-[7px] bg-white px-5 py-4">
					<p className="text-xs font-medium uppercase tracking-widest text-zinc-500">
						Step {stepIndex + 1} of {total}
					</p>
					<h1 className="text-brand-gradient text-2xl font-semibold tracking-tight sm:text-3xl">
						{step.title}
					</h1>
					<p className="text-sm text-zinc-500">{step.subtitle}</p>
				</div>
			</div>
			<div
				aria-hidden
				className="h-1 w-full overflow-hidden rounded-[6px] bg-zinc-100"
			>
				<div
					className="h-full"
					style={{
						width: `${progressPct}%`,
						backgroundImage:
							"linear-gradient(90deg, var(--color-brand-emerald) 0%, var(--color-brand-sky) 100%)",
						transition: "width 200ms ease",
					}}
				/>
			</div>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Step 1 — Basics
// ---------------------------------------------------------------------------

function StepBasics({
	form,
	errors,
	templates,
	templatesLoading,
	templatesError,
	onChangeBasics,
	onPickTemplate,
}: {
	form: FormState;
	errors: FieldErrors;
	templates: TemplateListItem[];
	templatesLoading: boolean;
	templatesError: string | null;
	onChangeBasics: (patch: Partial<BasicsPayload>) => void;
	onPickTemplate: (t: TemplateListItem) => void;
}) {
	return (
		<section className="flex flex-col gap-6">
			<FieldRow
				label="Campaign name"
				htmlFor="campaign-name"
				error={errors.name}
			>
				<input
					id="campaign-name"
					name="name"
					type="text"
					value={form.basics.name}
					onChange={(e) => onChangeBasics({ name: e.target.value })}
					placeholder="Spring US launch"
					className={inputClass(errors.name !== undefined)}
					autoComplete="off"
					maxLength={120}
				/>
			</FieldRow>

			<FieldRow
				label="Description"
				htmlFor="campaign-description"
				error={errors.description}
				optional
			>
				<textarea
					id="campaign-description"
					name="description"
					value={form.basics.description ?? ""}
					onChange={(e) =>
						onChangeBasics({ description: e.target.value })
					}
					placeholder="Short note for your team"
					className={`${inputClass(errors.description !== undefined)} min-h-[88px] resize-y`}
					maxLength={500}
				/>
			</FieldRow>

			<FieldRow label="Template" error={errors.offer_id}>
				{templatesLoading ? (
					<p className="text-sm text-zinc-500">Loading templates…</p>
				) : templatesError ? (
					<ErrorBanner
						title="Could not load templates"
						message={templatesError}
					/>
				) : templates.length === 0 ? (
					<p className="text-sm text-zinc-500">
						No templates available yet. Create one from the catalog first.
					</p>
				) : (
					<ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						{templates.map((t) => {
							const active = form.basics.offer_id === t.template_code;
							return (
								<li key={t.id}>
									<button
										type="button"
										onClick={() => onPickTemplate(t)}
										className={templateCardClass(active)}
										aria-pressed={active}
									>
										<span className="flex items-center justify-between gap-2">
											<span className="text-sm font-semibold text-zinc-900">
												{t.name}
											</span>
											<span className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-600">
												{t.scope}
											</span>
										</span>
										<span className="mt-1 block text-xs text-zinc-500">
											{t.vertical ?? "general"} ·{" "}
											<span className="font-mono">{t.template_code}</span>
										</span>
										{t.description ? (
											<span className="mt-2 block text-xs text-zinc-500">
												{t.description}
											</span>
										) : null}
									</button>
								</li>
							);
						})}
					</ul>
				)}
			</FieldRow>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Step 2 — Targeting overview (+ light-weight payout inline)
// ---------------------------------------------------------------------------

function StepTargeting({
	form,
	errors,
	onChangeRaw,
	onChangePayout,
}: {
	form: FormState;
	errors: FieldErrors;
	onChangeRaw: (raw: string) => void;
	onChangePayout: (patch: Partial<PayoutPayload>) => void;
}) {
	const payoutModels: PayoutModel[] = ["cpa", "cpi", "cps", "cpl", "revshare"];
	return (
		<section className="flex flex-col gap-6">
			<FieldRow
				label="Countries (ISO-2)"
				htmlFor="campaign-countries"
				error={errors.countries}
				hint="Comma separated — e.g. US, CA, GB"
			>
				<input
					id="campaign-countries"
					name="countries"
					type="text"
					value={form.targetingRaw}
					onChange={(e) => onChangeRaw(e.target.value.toUpperCase())}
					placeholder="US, CA, GB"
					className={inputClass(errors.countries !== undefined)}
					autoComplete="off"
				/>
			</FieldRow>

			<div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
				<FieldRow label="Payout model" htmlFor="campaign-payout-model">
					<select
						id="campaign-payout-model"
						className={inputClass(false)}
						value={form.payout.model}
						onChange={(e) =>
							onChangePayout({ model: e.target.value as PayoutModel })
						}
					>
						{payoutModels.map((m) => (
							<option key={m} value={m}>
								{m.toUpperCase()}
							</option>
						))}
					</select>
				</FieldRow>
				<FieldRow label="Amount" htmlFor="campaign-payout-amount">
					<input
						id="campaign-payout-amount"
						name="amount"
						type="number"
						min={0}
						step="0.01"
						value={Number.isFinite(form.payout.amount) ? form.payout.amount : 0}
						onChange={(e) =>
							onChangePayout({ amount: Number(e.target.value) })
						}
						className={inputClass(false)}
					/>
				</FieldRow>
				<FieldRow label="Currency" htmlFor="campaign-payout-currency">
					<input
						id="campaign-payout-currency"
						name="currency"
						type="text"
						value={form.payout.currency}
						onChange={(e) =>
							onChangePayout({
								currency: e.target.value.toUpperCase().slice(0, 3),
							})
						}
						maxLength={3}
						className={inputClass(false)}
					/>
				</FieldRow>
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Step 3 — Review
// ---------------------------------------------------------------------------

function StepReview({
	form,
	template,
	confirmed,
	error,
	onToggleConfirm,
}: {
	form: FormState;
	template: TemplateListItem | null;
	confirmed: boolean;
	error: string | null;
	onToggleConfirm: (v: boolean) => void;
}) {
	const countries = parseCountryList(form.targetingRaw);
	return (
		<section className="flex flex-col gap-6">
			<dl className="grid grid-cols-1 gap-4 rounded-[8px] border border-zinc-200 bg-white p-5 shadow-[var(--shadow-brand-subtle)] sm:grid-cols-2">
				<ReviewItem label="Name" value={form.basics.name || "—"} />
				<ReviewItem
					label="Template"
					value={template?.name ?? form.basics.offer_id ?? "—"}
				/>
				<ReviewItem
					label="Countries"
					value={countries.length > 0 ? countries.join(", ") : "—"}
				/>
				<ReviewItem
					label="Payout"
					value={`${form.payout.model.toUpperCase()} · ${form.payout.amount} ${form.payout.currency}`}
				/>
				{form.basics.description ? (
					<ReviewItem
						label="Description"
						value={form.basics.description}
						full
					/>
				) : null}
			</dl>

			<label className="flex cursor-pointer items-start gap-3 rounded-[8px] border border-zinc-200 bg-white p-4 hover:border-zinc-300">
				<input
					type="checkbox"
					checked={confirmed}
					onChange={(e) => onToggleConfirm(e.target.checked)}
					className="mt-0.5 h-4 w-4 rounded-[4px] border border-zinc-300 accent-emerald-500"
				/>
				<span className="flex flex-col gap-1">
					<span className="text-sm font-medium text-zinc-900">
						I confirm these details
					</span>
					<span className="text-xs text-zinc-500">
						Creating activates the campaign immediately.
					</span>
				</span>
			</label>
			{error ? <p className="text-xs text-red-600">{error}</p> : null}
		</section>
	);
}

function ReviewItem({
	label,
	value,
	full = false,
}: {
	label: string;
	value: string;
	full?: boolean;
}) {
	return (
		<div className={full ? "sm:col-span-2" : ""}>
			<dt className="text-xs font-medium uppercase tracking-wide text-zinc-500">
				{label}
			</dt>
			<dd className="mt-1 break-words text-sm text-zinc-900">{value}</dd>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Footer (back / next / create)
// ---------------------------------------------------------------------------

function WizardFooter({
	step,
	canGoBack,
	isSubmitting,
	onBack,
}: {
	step: UiStep;
	canGoBack: boolean;
	isSubmitting: boolean;
	onBack: () => void;
}) {
	const primaryLabel =
		step === "review"
			? isSubmitting
				? "Creating…"
				: "Create campaign"
			: "Next";
	return (
		<div className="flex items-center justify-between border-t border-zinc-200 pt-6">
			<button
				type="button"
				onClick={onBack}
				disabled={!canGoBack || isSubmitting}
				className={secondaryBtnClass(!canGoBack || isSubmitting)}
			>
				Back
			</button>
			<button
				type="submit"
				disabled={isSubmitting}
				className={primaryBtnClass(isSubmitting)}
			>
				{primaryLabel}
			</button>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Primitives — tiny inline form + button helpers (Tailwind 4, no shadcn dep)
// ---------------------------------------------------------------------------

function FieldRow({
	label,
	htmlFor,
	error,
	hint,
	optional,
	children,
}: {
	label: string;
	htmlFor?: string;
	error?: string;
	hint?: string;
	optional?: boolean;
	children: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<label
				htmlFor={htmlFor}
				className="flex items-center gap-2 text-sm font-medium text-zinc-800"
			>
				<span>{label}</span>
				{optional ? (
					<span className="text-xs font-normal text-zinc-400">(optional)</span>
				) : null}
			</label>
			{children}
			{hint && !error ? (
				<p className="text-xs text-zinc-500">{hint}</p>
			) : null}
			{error ? <p className="text-xs text-red-600">{error}</p> : null}
		</div>
	);
}

function ErrorBanner({ title, message }: { title: string; message: string }) {
	return (
		<div
			role="alert"
			className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3"
		>
			<p className="text-sm font-semibold text-red-700">{title}</p>
			<p className="mt-1 text-xs text-red-600">{message}</p>
		</div>
	);
}

function inputClass(error: boolean): string {
	const base =
		"w-full rounded-[6px] border bg-white px-3 py-2 text-sm text-zinc-900 shadow-[var(--shadow-brand-subtle)] outline-none transition placeholder:text-zinc-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
	const border = error ? "border-red-300" : "border-zinc-200";
	return `${base} ${border}`;
}

function templateCardClass(active: boolean): string {
	const base =
		"flex w-full flex-col rounded-[8px] border bg-white p-4 text-left transition hover:border-zinc-300 hover:shadow-[var(--shadow-brand-card)]";
	const state = active
		? "border-emerald-500 ring-2 ring-emerald-500/20"
		: "border-zinc-200";
	return `${base} ${state}`;
}

function primaryBtnClass(disabled: boolean): string {
	const base =
		"inline-flex items-center justify-center rounded-[8px] px-5 py-2 text-sm font-semibold text-white shadow-[var(--shadow-brand-subtle)] transition";
	const state = disabled
		? "opacity-60 cursor-not-allowed"
		: "hover:brightness-105";
	return `${base} ${state} bg-gradient-to-r from-emerald-500 to-sky-500`;
}

function secondaryBtnClass(disabled: boolean): string {
	const base =
		"inline-flex items-center justify-center rounded-[8px] border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition";
	const state = disabled
		? "opacity-60 cursor-not-allowed"
		: "hover:border-zinc-300 hover:bg-zinc-50";
	return `${base} ${state}`;
}
